import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Container from "@/components/containers/Container";
import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import { useHasPermission } from "@/hooks/useHasPermission";
import type { ChildRouteContext } from "@/root";
import { throwIfHasNoPermission } from "@/utils/permissions";

import ReviewFormList from "@/modules/performance/admin/components/review-form/ReviewFormList";
import ReviewPeriodList from "@/modules/performance/admin/components/review-period/ReviewPeriodList";
import { fetchReviewPeriodsQueryOptions } from "@/modules/performance/admin/services";
import { periodStatuses } from "@/modules/performance/shared/constants";

const tabs = [...periodStatuses, "form"] as const;

const searchSchema = z.object({
  tab: z.enum(tabs).optional(),
});

export const Route = createFileRoute(
  "/admin/_panel/_performance/performance/reviews/periods/",
)({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: ({ context }) => {
    throwIfHasNoPermission(context.user, [
      "performance.performance-review-period.view",
      "performance.performance-review-form.view",
    ]);

    return {
      crumb: [
        {
          label: context.t("Objective Setting Cycles", {
            ns: "performance.admin",
          }),
        },
      ],
    } satisfies ChildRouteContext;
  },
  loader: (opts) =>
    opts.context.queryClient.prefetchQuery(fetchReviewPeriodsQueryOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation("performance.admin");
  const navigate = Route.useNavigate();
  const { tab: status } = Route.useSearch();

  const hasPermission = useHasPermission();

  return (
    <Container
      title={t("Performance Review Periods")}
      description={t(
        "Using this section, you can create an evaluation period for users.",
      )}
    >
      <TabGroup
        defaultIndex={status ? tabs.indexOf(status) + 1 : 0}
        onChange={(index) =>
          navigate({
            search:
              index === 0
                ? {}
                : {
                    tab: tabs[index - 1],
                  },
          })
        }
      >
        <TabList>
          {hasPermission("performance.performance-review-period.view") && (
            <>
              <Tab>{t("All")}</Tab>
              <Tab>{t("Active")}</Tab>
              <Tab>{t("Disable", { ns: "core.shared" })}</Tab>
              <Tab>{t("Finished")}</Tab>
              <Tab>{t("Pending")}</Tab>
            </>
          )}
          {hasPermission("performance.performance-review-form.view") && (
            <Tab>{t("Question Template")}</Tab>
          )}
        </TabList>
        <TabPanels>
          {hasPermission("performance.performance-review-period.view") && (
            <>
              <TabPanel>
                <ReviewPeriodList />
              </TabPanel>
              <TabPanel>
                <ReviewPeriodList status="active" />
              </TabPanel>
              <TabPanel>
                <ReviewPeriodList status="disabled" />
              </TabPanel>
              <TabPanel>
                <ReviewPeriodList status="finished" />
              </TabPanel>
              <TabPanel>
                <ReviewPeriodList status="pending" />
              </TabPanel>
            </>
          )}
          {hasPermission("performance.performance-review-form.view") && (
            <TabPanel>
              <ReviewFormList />
            </TabPanel>
          )}
        </TabPanels>
      </TabGroup>
    </Container>
  );
}
